import { Component } from '@theme/component';

// The unfragmented page is the document index; existing hashes open a reader.
class LegalHub04 extends Component {
  #controller;
  #headerObserver;
  #resizeObserver;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#dispose();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dispose();
  }

  #initialize() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    for (const tab of this.#tabs()) {
      const panel = this.querySelector(`[id="${tab.dataset.legalPanel}"]`);
      panel?.setAttribute('role', 'region');
      panel?.setAttribute('aria-labelledby', tab.id);
    }
    const fromHash = this.#fromHash();
    this.#select(fromHash, { scroll: Boolean(fromHash) });
    window.addEventListener('hashchange', this.#onHash, { signal });
    window.addEventListener('popstate', this.#onHash, { signal });
    window.addEventListener('resize', this.#scheduleOffset, { signal });
    window.addEventListener('scroll', this.#scheduleOffset, { signal, passive: true, capture: true });
    document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
    const header = document.getElementById('header-component');
    if (header) {
      this.#headerObserver = new MutationObserver(this.#scheduleOffset);
      this.#headerObserver.observe(header, { attributes: true, attributeFilter: ['data-sticky-state', 'sticky'] });
      this.#resizeObserver = new ResizeObserver(this.#scheduleOffset);
      this.#resizeObserver.observe(header);
    }
    this.#setOffset();
  }

  #dispose() {
    this.#controller?.abort();
    this.#headerObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #tabs() {
    return Array.from(this.querySelectorAll('.legal04__tab'));
  }

  #fromHash() {
    const key = window.location.hash.slice(1);
    return this.#tabs().find((tab) => tab.dataset.legalKey === key);
  }

  #onHash = () => {
    this.#select(this.#fromHash(), { scroll: true });
  };

  #onEditorSelect = (event) => {
    const tab = this.#tabs().find((item) => item.dataset.blockId === event.detail?.blockId);
    if (tab) this.#select(tab, { scroll: true });
  };

  selectDocument(event) {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('.legal04__tab, [data-legal-card]');
    if (!link || !this.contains(link)) return;
    const key = link.dataset.legalKey || link.dataset.legalCard;
    const tab = this.#tabs().find((item) => item.dataset.legalKey === key);
    if (!tab) return;
    event.preventDefault();
    this.#select(tab, { history: true, scroll: true, focus: true });
  }

  showIndex(event) {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.#select(null, { history: true, scroll: true, focus: true });
  }

  navigateDocuments(event) {
    const tab = event.target.closest('.legal04__tab');
    if (!tab) return;
    const tabs = this.#tabs();
    const current = tabs.indexOf(tab);
    let next;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') next = (current + (rtl ? -1 : 1) + tabs.length) % tabs.length;
    if (event.key === 'ArrowLeft') next = (current + (rtl ? 1 : -1) + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (event.key === ' ') next = current;
    if (next === undefined) return;
    event.preventDefault();
    tabs[next].focus({ preventScroll: true });
    this.#select(tabs[next], { history: true, scroll: true });
  }

  #select(tab, options = {}) {
    const documentView = Boolean(tab);
    this.dataset.view = documentView ? 'document' : 'index';
    if (this.refs.index) this.refs.index.hidden = documentView;
    if (this.refs.documents) this.refs.documents.hidden = !documentView;
    if (this.refs.indexIntro) this.refs.indexIntro.hidden = documentView;
    if (this.refs.heading) {
      this.refs.heading.textContent = documentView ? tab.dataset.documentTitle : this.refs.heading.dataset.indexTitle;
    }
    if (this.refs.documentIntro) {
      this.refs.documentIntro.textContent = tab?.dataset.documentSummary || '';
      this.refs.documentIntro.hidden = !documentView || !tab.dataset.documentSummary;
    }
    if (this.refs.home) {
      if (documentView) this.refs.home.removeAttribute('aria-current');
      else this.refs.home.setAttribute('aria-current', 'page');
    }
    for (const item of this.#tabs()) {
      const active = item === tab;
      if (active) item.setAttribute('aria-current', 'page');
      else item.removeAttribute('aria-current');
      const panel = this.querySelector(`[id="${item.dataset.legalPanel}"]`);
      if (panel) panel.hidden = !active;
    }
    const rail = this.refs.tablist;
    if (tab && rail) {
      const rect = tab.getBoundingClientRect();
      const container = rail.getBoundingClientRect();
      const delta = rect.left < container.left ? rect.left - container.left - 16 : rect.right > container.right ? rect.right - container.right + 16 : 0;
      if (delta) rail.scrollBy({ left: delta, behavior: 'instant' });
    }
    const hash = tab ? `#${tab.dataset.legalKey}` : '';
    if (options.history && window.location.hash !== hash) {
      const url = new URL(window.location.href);
      url.hash = hash;
      window.history.pushState(window.history.state, '', url);
    }
    if (options.scroll) this.#scrollToView();
    if (options.focus) this.refs.heading?.focus({ preventScroll: true });
  }

  #scheduleOffset = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#setOffset();
    });
  };

  #setOffset() {
    const header = document.getElementById('header-component');
    const hidden = !header || header.dataset.stickyState === 'idle' || getComputedStyle(header).opacity === '0';
    const bottom = hidden || !header.hasAttribute('sticky') ? 0 : Math.max(0, header.getBoundingClientRect().bottom);
    const value = `${Math.round(bottom)}px`;
    if (this.style.getPropertyValue('--legal04-top') !== value) this.style.setProperty('--legal04-top', value);
  }

  #scrollToView() {
    this.#setOffset();
    const wrapper = this.closest('.page-wrapper');
    const scroller = wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper : window;
    const scrollTop = scroller === window ? window.scrollY : scroller.scrollTop;
    const origin = scroller === window ? 0 : scroller.getBoundingClientRect().top;
    const offset = parseFloat(this.style.getPropertyValue('--legal04-top')) || 0;
    const top = Math.max(0, scrollTop + this.getBoundingClientRect().top - origin - offset);
    scroller.scrollTo({ top, behavior: 'instant' });
  }
}

if (!customElements.get('legal-hub-04')) customElements.define('legal-hub-04', LegalHub04);
